import { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDayTimeIn } from "@/lib/datetime";
import { employeeApi, type NotificationItem } from "@/lib/employee-api";

/** The app's Home shows three; so does the web (FEAT-2026-024). */
const PREVIEW_LIMIT = 3;

/**
 * The latest announcements, as on the app's Home.
 *
 * Hidden when there are none — and when they cannot be loaded, because this is
 * secondary to the status card and must never take the page down with it. The
 * full list, and marking read, live on the Notifications page.
 */
export function AnnouncementsCard({ tz }: { tz: string }) {
  const [items, setItems] = useState<NotificationItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    employeeApi
      .notifications({ type: "ANNOUNCEMENT", pageSize: PREVIEW_LIMIT })
      .then((res) => {
        if (!cancelled) setItems(res.data);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <Card data-testid="today-announcements">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Megaphone className="h-4 w-4 text-muted-foreground" />
          Announcements
        </CardTitle>
        <Link to="/me/notifications" className="text-sm font-medium text-primary hover:underline">
          View all
        </Link>
      </CardHeader>
      <CardContent className="divide-y p-0">
        {items.map((n) => (
          <div key={n.id} className="space-y-1 px-6 py-3">
            <div className="flex items-center gap-2">
              <p className={n.isRead ? "text-sm" : "text-sm font-semibold"}>{n.title}</p>
              {!n.isRead && (
                <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                  New
                </Badge>
              )}
            </div>
            <p className="line-clamp-2 text-sm text-muted-foreground">{n.body}</p>
            <p className="text-xs text-muted-foreground">{formatDayTimeIn(n.createdAt, tz)}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
